import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Col, Form, Row, Button, Modal, ListGroup } from 'react-bootstrap';
import PropTypes from 'prop-types';
import axios from 'helpers/api';
import { toast } from 'react-toastify';
import Spinner from 'react-bootstrap/Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createIframeReportBlobUrl } from 'helpers/iframeReportPreview';
import moment from 'moment';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import isHiddenFacility from 'helpers/isHiddenFacility';

const GRID_URL = `${process.env.REACT_APP_SERVER_URL}/reports/users-contact-list/`;
const REPORT_URL = `${process.env.REACT_APP_SERVER_URL}/reports/users-contact-list-report`;

const YES_NO_ALL = [
    { key: 'All', value: '' },
    { key: 'Yes', value: '1' },
    { key: 'No', value: '0' }
];

// API sends "Yes" / "No" strings for web_access, mobile_access, active_status
const isYes = value => value === 1 || value === '1' || value === true || String(value).toLowerCase() === 'yes';

const YesNoIcon = ({ value }) =>
    isYes(value)
        ? <FontAwesomeIcon icon="check" className="ms-1 text-success" />
        : <FontAwesomeIcon icon="times-circle" className="text-danger" />;

YesNoIcon.propTypes = { value: PropTypes.any };

const textCol = (accessorKey, header) => ({
    accessorKey,
    header,
    meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
    }
});

const flagCol = (accessorKey, header) => ({
    accessorKey,
    header,
    cell: ({ row: { original } }) => <YesNoIcon value={original[accessorKey]} />
});

// Map one API user to a grid row. Adjust field names here if the API differs.
const toRow = user => ({
    name: user.full_name,
    username: user.username || '',
    email: user.email || '',
    phone: user.phone || user.mobile || user.phone_number || '',
    role: user.role_name || user.role || '',
    facility_names: Array.isArray(user.facility_names) ? user.facility_names.join(', ') : (user.facility_names || ''),
    facility_list: Array.isArray(user.facility_names) ? user.facility_names : [],
    never_logged: !user.last_login_timestamp,
    is_active: user.active_status,
    web_access: user.web_access,
    mobile_access: user.mobile_access,
    last_login: user.last_login_timestamp ? moment(user.last_login_timestamp).format('MM/DD/YYYY hh:mm A') : 'Never'
});

// Blob error bodies (responseType: 'blob') need to be read before we can show the message.
const readBlobMessage = async blob => {
    try {
        const text = await blob.text();
        const json = JSON.parse(text);
        return json.message || json.error;
    } catch (e) {
        return null;
    }
};

const getFilename = (headers, fallback) => {
    const disposition = headers?.['content-disposition'];
    return disposition
        ? disposition.split('filename=')[1]?.replace(/"/g, '').trim() || fallback
        : fallback;
};

const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    URL.revokeObjectURL(url);
};

const ALL_GROUPS = { key: 'All Groups', value: '' };
const ALL_FACILITIES = { key: 'All Facilities', value: '' };

const readStoredList = key => {
    try {
        return JSON.parse(localStorage.getItem(key)) || [];
    } catch (e) {
        return [];
    }
};

// Same look as the other report dropdowns
const selectStyles = {
    control: provided => ({
        ...provided,
        borderColor: '#ced4da',
        boxShadow: 'none',
        '&:hover': { borderColor: '#80bdff' }
    }),
    option: (provided, { isFocused, isSelected }) => ({
        ...provided,
        backgroundColor: isSelected ? '#e9ecef !important' : isFocused ? '#f8f9fa !important' : 'white !important',
        color: '#212529 !important',
        cursor: 'pointer'
    }),
    singleValue: provided => ({ ...provided, color: '#212529' })
};

// Facilities shown inline in the grid; the rest open in a modal.
const MAX_INLINE_FACILITIES = 1;

const UserContactListReport = () => {
    const [facilityModalRow, setFacilityModalRow] = useState(null);
    const [facilitySearch, setFacilitySearch] = useState('');

    const openFacilityModal = row => {
        setFacilitySearch('');
        setFacilityModalRow(row);
    };
    const closeFacilityModal = () => {
        setFacilityModalRow(null);
        setFacilitySearch('');
    };
    const [loader, setLoader] = useState(false);
    const [pdfLoader, setPdfLoader] = useState(false);
    const [csvLoader, setCsvLoader] = useState(false);
    const [iframeSrc, setIframeSrc] = useState(null);
    const [allRows, setAllRows] = useState([]);
    const [facility, setFacility] = useState(null);
    const [scopeLabel, setScopeLabel] = useState('');
    const tableRef = useRef(null);
    // Group + facility pickers (replace the old "All Facility" checkbox)
    const [groupOptions] = useState(() => [ALL_GROUPS, ...readStoredList('groupsData')]);
    const [allFacilities] = useState(() => readStoredList('facilitiesData').filter(f => !isHiddenFacility(f)));
    const [selectedGroup, setSelectedGroup] = useState(ALL_GROUPS);
    const [selectedFacilityOption, setSelectedFacilityOption] = useState(ALL_FACILITIES);

    // Facility list follows the selected group
    const facilityOptions = useMemo(() => [
        ALL_FACILITIES,
        ...(selectedGroup.value === ''
            ? allFacilities
            : allFacilities.filter(f => String(f.group_id) === String(selectedGroup.value)))
    ], [allFacilities, selectedGroup]);

    const handleGroupChange = option => {
        setSelectedGroup(option || ALL_GROUPS);
        setSelectedFacilityOption(ALL_FACILITIES); // reset facility when group changes
    };
    const [filters, setFilters] = useState({
        is_active: '',
        web_access: '',
        mobile_access: '',
        never_logged: false
    });

    const columns = [
        textCol('name', 'Name'),
        textCol('username', 'Username'),
        textCol('email', 'Email'),
        textCol('phone', 'Phone'),
        textCol('role', 'Role'),
        {
            // accessorKey keeps the full list searchable
            accessorKey: 'facility_names',
            header: 'Facilities',
            meta: {
                headerProps: { className: 'text-900 text-start' },
                cellProps: { className: 'text-start' }
            },
            cell: ({ row: { original } }) => {
                const list = original.facility_list || [];
                if (list.length === 0) return '-';
                const extra = list.length - MAX_INLINE_FACILITIES;
                return (
                    <span>
                        {list.slice(0, MAX_INLINE_FACILITIES).join(', ')}
                        {extra > 0 && (
                            <Button
                                variant="link"
                                size="sm"
                                className="p-0 ms-1 align-baseline fs-11"
                                onClick={() => openFacilityModal(original)}
                            >
                                +{extra} more
                            </Button>
                        )}
                    </span>
                );
            }
        },
        flagCol('is_active', 'Active'),
        flagCol('web_access', 'Web Access'),
        flagCol('mobile_access', 'Mobile Access'),
        textCol('last_login', 'Last Login')
    ];

    // Facility wins over group: a selected facility sends facility_id only,
    // otherwise a selected group sends group_id, otherwise facility_id '' (all).
    const buildScope = () => {
        if (selectedFacilityOption.value !== '') {
            return { facility_id: selectedFacilityOption.value };
        }
        if (selectedGroup.value !== '') {
            return { group_id: selectedGroup.value };
        }
        return { facility_id: '' };
    };

    // Download / CSV send the same scope plus every filter so the report matches the grid.
    const buildPayload = () => {
        const payload = {
            ...buildScope(),
            is_active: filters.is_active,
            web_access: filters.web_access,
            mobile_access: filters.mobile_access
        };
        if (filters.never_logged) {
            payload.never_logged = 1;
        }
        return payload;
    };

    // Grid is fetched only when the group or facility changes; other filters are applied locally.
    useEffect(() => {
        fetchData();
    }, [selectedGroup, selectedFacilityOption]);

    const fetchData = async () => {
        setLoader(true);
        try {
            const response = await axios.post(GRID_URL, buildScope());
            if (response.data?.status === false) {
                toast.error(response.data.message);
                setAllRows([]);
                setFacility(null);
                setScopeLabel('');
                return;
            }
            const payload = response.data?.data ?? response.data ?? {};
            setAllRows((payload.users || []).map(toRow));
            setFacility(payload.facility || null);
            setScopeLabel(payload.scope_label || '');
        } catch (err) {
            const apiMessage = err?.response?.data?.message || err?.response?.data?.error;
            toast.error(apiMessage || 'Failed to load user contact list.');
            setAllRows([]);
            setFacility(null);
            setScopeLabel('');
        } finally {
            setLoader(false);
        }
    };

    const handleDownload = async (asCsv) => {
        const setBusy = asCsv ? setCsvLoader : setPdfLoader;
        setBusy(true);
        try {
            const body = asCsv ? { ...buildPayload(), is_print: 'csv' } : buildPayload();
            const response = await axios.post(REPORT_URL, body, { responseType: 'blob' });
            const blob = response.data;
            const contentType = (response.headers?.['content-type'] || blob.type || '').toLowerCase();

            if (contentType.includes('application/json')) {
                const message = await readBlobMessage(blob);
                toast.error(message || 'Failed to generate report.');
                return;
            }

            if (asCsv) {
                downloadBlob(blob, getFilename(response.headers, 'users-contact-list.csv'));
                toast.success('User Contact List Exported Successfully');
            } else if (contentType.includes('text/html')) {
                // HTML report: print through the hidden iframe like the other reports.
                const { blobUrl } = await createIframeReportBlobUrl(blob);
                setIframeSrc(blobUrl);
                toast.success('User Contact List Generated Successfully');
            } else {
                downloadBlob(blob, getFilename(response.headers, 'users-contact-list.pdf'));
                toast.success('User Contact List Generated Successfully');
            }
        } catch (err) {
            const data = err?.response?.data;
            const apiMessage = data instanceof Blob ? await readBlobMessage(data) : (data?.message || data?.error);
            toast.error(apiMessage || 'Failed to generate report.');
        } finally {
            setBusy(false);
        }
    };

    const handleFilterChange = e => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    // All filtering happens here, no API call on filter change.
    const tableData = useMemo(() => {
        const matches = (filterValue, rowValue) =>
            filterValue === '' || (filterValue === '1') === isYes(rowValue);
        return allRows.filter(row =>
            matches(filters.is_active, row.is_active) &&
            matches(filters.web_access, row.web_access) &&
            matches(filters.mobile_access, row.mobile_access) &&
            (!filters.never_logged || row.never_logged)
        );
    }, [allRows, filters]);

    // Counts follow the filtered grid.
    const summary = useMemo(() => {
        if (!allRows.length) return null;
        const active = tableData.filter(row => isYes(row.is_active)).length;
        const facilities = new Set(tableData.flatMap(row => row.facility_list));
        return {
            total_users: tableData.length,
            active_users: active,
            inactive_users: tableData.length - active,
            facilities: facilities.size
        };
    }, [allRows, tableData]);

    const table = useAdvanceTable({
        data: tableData,
        columns,
        selection: false,
        sortable: true,
        pagination: true
    });

    const facilityName = facility && (typeof facility === 'string'
        ? facility
        : facility.facility_name || facility.name);

    const summaryItems = summary ? [
        { label: 'Users', value: summary.total_users },
        { label: 'Active', value: summary.active_users },
        { label: 'Inactive', value: summary.inactive_users },
        { label: 'Facilities', value: summary.facilities }
    ] : [];

    const tableHeight = tableRef.current ? tableRef.current.clientHeight : 0;
    const busy = pdfLoader || csvLoader;

    return (
        <div className="mt-4">
            <Modal show={!!facilityModalRow} onHide={closeFacilityModal} centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title as="h5">
                        Facilities{facilityModalRow?.name ? ` - ${facilityModalRow.name}` : ''}
                        {facilityModalRow && (
                            <span className="text-600 fs-10 ms-2">({facilityModalRow.facility_list.length})</span>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Control
                        type="search"
                        size="sm"
                        placeholder="Search facility..."
                        value={facilitySearch}
                        onChange={e => setFacilitySearch(e.target.value)}
                        className="mb-2"
                        autoFocus
                    />
                    {(() => {
                        const term = facilitySearch.trim().toLowerCase();
                        const list = (facilityModalRow?.facility_list || [])
                            .filter(name => !term || String(name).toLowerCase().includes(term));
                        return list.length > 0 ? (
                            <ListGroup variant="flush">
                                {list.map((name, index) => (
                                    <ListGroup.Item key={`${name}-${index}`} className="px-0 py-2">
                                        {name}
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        ) : (
                            <p className="text-600 text-center mb-0 py-3">No facilities match "{facilitySearch}".</p>
                        );
                    })()}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeFacilityModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
            <Row className="mb-3">
                <Col md={6}>
                    <Form.Group>
                        <Form.Label>Group</Form.Label>
                        <ThemedSelect
                            name="group"
                            closeMenuOnSelect={true}
                            placeholder="Select Group"
                            classNamePrefix="react-select"
                            options={groupOptions}
                            value={selectedGroup}
                            getOptionLabel={option => option.key}
                            getOptionValue={option => option.value}
                            onChange={handleGroupChange}
                            styles={selectStyles}
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group>
                        <Form.Label>Facility</Form.Label>
                        <ThemedSelect
                            name="facility"
                            closeMenuOnSelect={true}
                            placeholder="Select Facility"
                            classNamePrefix="react-select"
                            options={facilityOptions}
                            value={selectedFacilityOption}
                            getOptionLabel={option => option.key}
                            getOptionValue={option => option.value}
                            onChange={option => setSelectedFacilityOption(option || ALL_FACILITIES)}
                            styles={selectStyles}
                        />
                    </Form.Group>
                </Col>
            </Row>
            <Row className="mb-3">
                <Col md={3}>
                    <Form.Group>
                        <Form.Label>Active</Form.Label>
                        <Form.Select name="is_active" size="sm" value={filters.is_active} onChange={handleFilterChange}>
                            {YES_NO_ALL.map(o => <option key={o.key} value={o.value}>{o.key}</option>)}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={3}>
                    <Form.Group>
                        <Form.Label>Web Access</Form.Label>
                        <Form.Select name="web_access" size="sm" value={filters.web_access} onChange={handleFilterChange}>
                            {YES_NO_ALL.map(o => <option key={o.key} value={o.value}>{o.key}</option>)}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={3}>
                    <Form.Group>
                        <Form.Label>Mobile Access</Form.Label>
                        <Form.Select name="mobile_access" size="sm" value={filters.mobile_access} onChange={handleFilterChange}>
                            {YES_NO_ALL.map(o => <option key={o.key} value={o.value}>{o.key}</option>)}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={3}>
                    <Form.Group>
                        
                        <Form.Check
                            type="switch"
                            className="mt-4"
                            name="never_logged"
                            id="never_logged"
                            checked={filters.never_logged}
                            onChange={e => setFilters({ ...filters, never_logged: e.target.checked })}
                            label="Only never logged in"
                        />
                    </Form.Group>
                </Col>
            </Row>

            {summaryItems.length > 0 && (
                <Row className="mb-3 g-2">
                    {summaryItems.map(item => (
                        <Col key={item.label} xs={6} md={3}>
                            <Card className="shadow-sm rounded-3 h-100">
                                <Card.Body className="py-2 px-3">
                                    <div className="fs-10 text-600">{item.label}</div>
                                    <div className="fs-6 fw-semibold">{item.value ?? 0}</div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            <AdvanceTableProvider {...table}>
                <Card className="mb-3 shadow-sm rounded-3">
                    <Card.Body className="p-4" ref={tableRef}>
                        {loader ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: `${tableHeight || 150}px` }}>
                                <Spinner animation="border" role="status" className="mx-auto"></Spinner>
                            </div>
                        ) : tableData.length > 0 ? (
                            <Row>
                                <Col xs="4" className="mb-2 view-patient-search-holder">
                                    <AdvanceTableSearchBox placeholder="Search..." />
                                </Col>
                                <Col xs="12">
                                    <AdvanceTable
                                        headerClassName="bg-200 text-nowrap align-middle text-center fw-semibold"
                                        rowClassName="text-nowrap align-middle text-center"
                                        tableProps={{
                                            size: 'sm',
                                            striped: true,
                                            className: 'fs-12 mb-0 overflow-hidden table-bordered shadow-sm'
                                        }}
                                    />
                                </Col>
                            </Row>
                        ) : (
                            <div className="d-flex justify-content-center align-items-center text-center" style={{ height: '150px' }}>
                                <p className="text-danger fw-bold mb-0">No users found for the selected filters.</p>
                            </div>
                        )}
                    </Card.Body>
                    <Card.Footer className="bg-light rounded-3 py-3">
                        <div className="mt-3">
                            <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
                        </div>
                    </Card.Footer>
                </Card>
            </AdvanceTableProvider>

            <Form>
                <Row>
                    <Col xs={6} className="text-center">
                        {pdfLoader ? (
                            <Button variant="primary" disabled className="mt-3 w-75 px-4 py-2">
                                <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                                <span className="ms-2">Loading...</span>
                            </Button>
                        ) : (
                            <Button disabled={busy} onClick={() => handleDownload(false)} color="primary" className="mt-3 w-75 px-4 py-2">
                                Download Report
                            </Button>
                        )}
                    </Col>
                    <Col xs={6} className="text-center">
                        {csvLoader ? (
                            <Button variant="primary" disabled className="mt-3 w-75 px-4 py-2">
                                <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                                <span className="ms-2">Loading...</span>
                            </Button>
                        ) : (
                            <Button disabled={busy} onClick={() => handleDownload(true)} color="primary" className="mt-3 w-75 px-4 py-2">
                                Export csv
                            </Button>
                        )}
                    </Col>
                </Row>
                <Row className="mt-3">
                    <Col>
                        <div style={{ height: 0, overflow: 'hidden', width: 0 }}>
                            {iframeSrc && (
                                <iframe
                                    src={iframeSrc}
                                    style={{ width: "100%", height: "400px", border: "none", borderRadius: "8px" }}
                                    title="Preview Content"
                                />
                            )}
                        </div>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};


export default UserContactListReport;
